import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

// components
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Select,
} from "antd";

// icons
import { CheckSquareFilled, LoadingOutlined } from "@ant-design/icons";

// utils
import { colorPalette } from "../theme";
import { useAxios } from "../hooks";
import { CountryList } from "../country";

const Container = styled.div`
  display: flex;
  padding: 24px;
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 130px;
  width: 90%;
  margin: 0px auto;

  @media (max-width: 479px) {
    flex-direction: column-reverse;
    gap: 20px;
    width: 100%;
  }
}
`;

const LeftSection = styled.div`
  width: 80%;
  display: flex;
  flex-direction: column;
  gap: 20px;

  h2 {
    font-size: 40px;
    color: rgb(52, 71, 103);
    font-weight: 600;
    line-height: 1.3;
    margin: 0px;
  }

  h5 {
    margin: 0px;
    font-weight: 400;
    font-size: 1.5rem;
    line-height: 1.375;
    color: rgb(85, 85, 85);
    opacity: 1;
  }

  @media (max-width: 479px) {
    width: 100%;
  }
`;

const StyledCard = styled(Card)`
  width: 80%;

  @media (max-width: 479px) {
    width: 100%;
  }
`;

const FeatureWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  div {
    display: flex;
    align-items: center;

    .anticon {
      margin-right: 10px;
      font-size: 35px;

      path {
        fill: ${colorPalette.primary[500]};
      }
    }

    span {
      font-weight: 600;
      font-size: 1.23rem;
      line-height: 24px;
      color: rgb(52, 71, 103);
      opacity: 1;
    }
  }
`;

const FormTitle = styled.h2`
  font-size: 28px;
  margin: 0px 0px 20px 0px;
  text-align: center;
  color: ${colorPalette.primary[500]};
`;

const FormItem = styled(Form.Item)`
  margin-bottom: 20px;

  .ant-form-item-explain-error {
    margin-bottom: 8px;
  }

  .ant-select {
    width: 150px;
  }

  .ant-otp {
    display: flex;
    justify-content: center;

    .ant-otp-input {
      padding: 8px;
      font-size: 24px;
    }
  }

  @media (max-width: 479px) {
    .ant-select {
      width: 130px;
    }
  }
`;

const TermsText = styled.p`
  font-size: 14px;
  text-align: center;
`;

const SignUp = () => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  const [form] = Form.useForm();
  const { fetch: onSubmit, loading } = useAxios();
  const { fetch: onResendOTP, loading: resendLoading } = useAxios();
  const { fetch: validateOTP, otpLoading } = useAxios();
  const [error, setError] = useState(null);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [onboardingId, setOnboardingId] = useState(null);

  const countryCodeList = CountryList.map((ele) => ({
    label: `${ele.dial_code} (${ele.name})`,
    value: ele.dial_code,
  }));

  const features = [
    "One month free trial",
    "No Credit Card required",
    "1 month free trial for unlimited users",
    "Integrate with HRMS and intracom.",
    "15 min demo",
    "10 hours premium support",
  ];

  useEffect(() => {
    if (otp.length === 5) {
      // Simulate OTP verification
      onValidateOtp();
    }
  }, [otp]);

  const handleKeyPress = (event) => {
    const { key, which } = event;
    if (
      !/^[0-9]$/.test(key) && // Allow digits 0-9
      which != 65 && // Allow all select
      key !== "Backspace" && // Allow Backspace
      key !== "ArrowLeft" && // Allow left arrow
      key !== "ArrowRight" && // Allow right arrow
      key !== "Delete" // Allow delete
    ) {
      event.preventDefault();
    }
  };

  const onValidateOtp = async () => {
    setError(null);
    try {
      await validateOTP({
        url: "/company/onboarding/otp/",
        method: "POST",
        data: { otp: otp, id: onboardingId },
      });
      setShowOtpModal(false);
      navigate(`/onboarding/${onboardingId}`);
    } catch (error) {
      setError(error.response?.data?.message || "OTP verification failed");
    }
  };

  const handleResendOtp = async () => {
    try {
      await onResendOTP({
        url: `/company/onboarding/${onboardingId}/resend-otp/`,
        method: "POST",
      });
      messageApi.open({
        type: "success",
        content: "OTP resent successfully",
      });
    } catch (error) {
      messageApi.open({
        type: "error",
        content: error.response?.data?.message || "OTP resend failed",
      });
    }
  };

  const onFinish = async (values) => {
    // console.log("Form values: ", values);
    setError(null);
    try {
      const { data } = await onSubmit({
        url: "/company/onboarding/signup/",
        method: "POST",
        data: values,
      });
      setOnboardingId(data?.id);
      setShowOtpModal(true);
      form.resetFields();
    } catch (error) {
      setError(error.response?.data?.message);
      console.error("Error submitting form: ", error);
    }
  };

  return (
    <>
      <Container>
        <Wrapper>
          <LeftSection>
            <h2>Create your free business account now!</h2>
            <h5>
              Signup and onboard your team instantly. No strings attached.
            </h5>
            <FeatureWrapper>
              {features.map((feature, index) => (
                <div key={index}>
                  <CheckSquareFilled />
                  <span>{feature}</span>
                </div>
              ))}
            </FeatureWrapper>
            <Divider />
          </LeftSection>
          <StyledCard>
            <FormTitle>Sign Up </FormTitle>
            {!showOtpModal && error && (
              <Alert
                message={error}
                type="error"
                showIcon
                style={{ marginBottom: 20 }}
              />
            )}
            <Form layout="vertical" form={form} onFinish={onFinish}>
              <FormItem
                name="full_name"
                label="Full Name"
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                  },
                ]}
              >
                <Input placeholder="Enter Full Name" />
              </FormItem>
              <FormItem
                name="email"
                label="Business Email"
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                  },
                  // {
                  //   type: "email",
                  //   message: "Please enter a valid email address",
                  // },
                  () => ({
                    validator(_, value) {
                      const emailRegex =
                        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                      if (!value) {
                        return Promise.resolve();
                      }
                      if (!emailRegex.test(value)) {
                        return Promise.reject(
                          new Error("Please enter a valid email address"),
                        );
                      }
                      const domain = value.split("@")[1] || "";
                      // const prefix = domain.split(".")[1] || "";
                      // if (prefix.length < 2) {
                      //   return Promise.resolve();
                      // }
                      if (
                        domain.includes("gmail") ||
                        domain.includes("yahoo") ||
                        domain.includes("outlook") ||
                        domain.includes("hotmail") ||
                        domain.includes("aol")
                      ) {
                        return Promise.reject(
                          new Error("Please use your business email"),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input placeholder="Enter Business Email" />
              </FormItem>
              <FormItem
                name="business_name"
                label="Business Name"
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                  },
                ]}
              >
                <Input placeholder="Enter Business Name" />
              </FormItem>
              <FormItem
                name="phone_number"
                label="Phone Number"
                rules={[
                  {
                    min: 9,
                    // message: t('form.phone_number.min'),
                  },
                  {
                    max: 12,
                    // message: t('form.phone_number.max'),
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value && getFieldValue("dialCode")) {
                        return Promise.reject(
                          new Error("Phone Number is required"),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  addonBefore={
                    <FormItem
                      name="dial_code"
                      noStyle
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value && getFieldValue("phoneNumber")) {
                              return Promise.reject(
                                new Error("Country Code is required"),
                              );
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <Select
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        options={countryCodeList}
                        placeholder="Country Code"
                      />
                    </FormItem>
                  }
                  placeholder="Enter Phone Number"
                  onKeyPress={handleKeyPress}
                />
              </FormItem>
              <FormItem
                name="referral_code"
                label="Referral Code"
                tooltip="Have a referral code? Enter it here to get an extra month free!"
              >
                <Input placeholder="Enter Referral Code" />
              </FormItem>
              <FormItem style={{ marginTop: "30px" }}>
                <Button
                  size="large"
                  block
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                >
                  SIGN ME UP
                </Button>
              </FormItem>
            </Form>
            <TermsText>
              By signing up with Elrin.ai, you acknowledge that you agree to our{" "}
              <a target="_blank" href="https://elrin.ai/tnc">
                Terms of Service
              </a>{" "}
              and{" "}
              <a target="_blank" href="https://elrin.ai/privacy-policy">
                Privacy Policy
              </a>
            </TermsText>
          </StyledCard>
        </Wrapper>
      </Container>
      <Modal
        open={showOtpModal}
        destroyOnHidden
        centered
        footer={null}
        width={420}
        closable={false}
        loading={otpLoading}
      >
        <FormTitle>OTP Verification</FormTitle>
        {showOtpModal && error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 20 }}
          />
        )}
        <p style={{ textAlign: "center" }}>
          An OTP has been sent to your registered email. <br />
          Please verify to complete the signup process.
        </p>
        <Form layout="vertical">
          <FormItem
            name="otp"
            rules={[
              {
                required: true,
                message: "This field is required",
              },
              {
                len: 5,
                message: "OTP must be 5 digits",
              },
            ]}
          >
            <Input.OTP
              length={5}
              value={otp}
              autoFocus
              onKeyPress={handleKeyPress}
              onInput={(e) => setOtp(e.join(""))}
              // onChange={setOtp}
            />
          </FormItem>
          <TermsText>
            Did not receive OTP ? <a onClick={handleResendOtp}>Resend</a>{" "}
            {resendLoading && <LoadingOutlined />}
          </TermsText>
        </Form>
      </Modal>
      {contextHolder}
    </>
  );
};

export default SignUp;
